import * as Sentry from '@sentry/react';
import CurrentUserStore from '../stores/CurrentUserStore';
import ShopStore from '../stores/ShopStore';

export interface MediaErrorContext {
  workOrderId?: string;
  mediaUrl?: string;
  mediaType?: string;
  fileName?: string;
  fileSize?: number;
  loadStartTime?: number;
  errorType:
    | 'timeout'
    | 'network'
    | 'permission'
    | 'format'
    | 'rendering'
    | 'unknown';
  errorMessage?: string;
  httpStatus?: number;
  userAgent?: string;
}

export interface MediaLoadMetrics {
  workOrderId?: string;
  mediaUrl?: string;
  mediaType?: string;
  loadTime: number;
  success: boolean;
  errorType?: string;
}

class MediaErrorTrackingService {
  private loadStartTimes = new Map<string, number>();
  private timeoutIds = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Track the start of media loading
   */
  trackLoadStart(
    mediaUrl: string,
    workOrderId?: string,
    mediaType?: string
  ): void {
    const key = this.getMediaKey(mediaUrl, workOrderId);
    const startTime = Date.now();

    this.loadStartTimes.set(key, startTime);

    // Set up timeout detection (10 seconds)
    const timeoutId = setTimeout(() => {
      this.trackMediaError({
        workOrderId,
        mediaUrl,
        mediaType,
        errorType: 'timeout',
        errorMessage: 'Media loading exceeded 10 second timeout',
        loadStartTime: startTime,
        userAgent: navigator.userAgent,
      });

      // Clean up tracking data
      this.cleanupTracking(key);
    }, 10000);

    this.timeoutIds.set(key, timeoutId);

    // Add Sentry breadcrumb for load start
    Sentry.addBreadcrumb({
      message: 'Media load started',
      category: 'media',
      level: 'info',
      data: {
        workOrderId,
        mediaUrl,
        mediaType,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Track successful media load
   */
  trackLoadSuccess(
    mediaUrl: string,
    workOrderId?: string,
    mediaType?: string
  ): void {
    const key = this.getMediaKey(mediaUrl, workOrderId);
    const startTime = this.loadStartTimes.get(key);

    if (startTime) {
      const loadTime = Date.now() - startTime;

      // Track performance metrics
      this.trackMediaMetrics({
        workOrderId,
        mediaUrl,
        mediaType,
        loadTime,
        success: true,
      });

      // Add success breadcrumb
      Sentry.addBreadcrumb({
        message: 'Media loaded successfully',
        category: 'media',
        level: 'info',
        data: {
          workOrderId,
          mediaUrl,
          mediaType,
          loadTime,
          timestamp: new Date().toISOString(),
        },
      });
    }

    this.cleanupTracking(key);
  }

  /**
   * Track media loading error
   */
  trackMediaError(context: MediaErrorContext): void {
    const {
      workOrderId,
      mediaUrl,
      mediaType,
      errorType,
      errorMessage,
      httpStatus,
      loadStartTime,
    } = context;

    const key = this.getMediaKey(mediaUrl || '', workOrderId);
    const startTime = loadStartTime || this.loadStartTimes.get(key);
    const loadTime = startTime ? Date.now() - startTime : undefined;

    // Get user and shop context
    const userContext = this.getUserContext();
    const shopContext = this.getShopContext();

    // Track metrics for failed loads
    if (loadTime) {
      this.trackMediaMetrics({
        workOrderId,
        mediaUrl,
        mediaType,
        loadTime,
        success: false,
        errorType,
      });
    }

    // Capture error to Sentry
    Sentry.captureException(new Error(`Media loading failed: ${errorType}`), {
      tags: {
        component: 'MediaPreview',
        errorType,
        mediaType: mediaType || 'unknown',
        section: 'work-order-details',
      },
      contexts: {
        media: {
          url: mediaUrl,
          type: mediaType,
          errorType,
          httpStatus,
          loadTime,
        },
        workOrder: {
          id: workOrderId,
        },
        user: userContext,
        shop: shopContext,
        browser: {
          userAgent: navigator.userAgent,
          connectionType: this.getConnectionType(),
          timestamp: new Date().toISOString(),
        },
      },
      extra: {
        errorMessage,
        mediaUrl,
        workOrderId,
        loadStartTime: startTime,
        loadDuration: loadTime,
      },
    });

    // Add error breadcrumb
    Sentry.addBreadcrumb({
      message: `Media load failed: ${errorType}`,
      category: 'media',
      level: 'error',
      data: {
        workOrderId,
        mediaUrl,
        errorType,
        errorMessage,
        loadTime,
        timestamp: new Date().toISOString(),
      },
    });

    this.cleanupTracking(key);
  }

  /**
   * Track stuck loading states (spinner showing too long)
   */
  trackStuckSpinner(
    workOrderId: string,
    mediaUrl?: string,
    duration?: number
  ): void {
    this.trackMediaError({
      workOrderId,
      mediaUrl,
      errorType: 'timeout',
      errorMessage: `Media spinner stuck for ${duration || 'unknown'} seconds`,
      loadStartTime: Date.now() - (duration ? duration * 1000 : 10000),
    });
  }

  /**
   * Clean up tracking data for a media item
   */
  private cleanupTracking(key: string): void {
    // Clear timeout
    const timeoutId = this.timeoutIds.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeoutIds.delete(key);
    }

    // Remove load start time
    this.loadStartTimes.delete(key);
  }

  /**
   * Generate unique key for media tracking
   */
  private getMediaKey(mediaUrl: string, workOrderId?: string): string {
    return `${workOrderId || 'no-wo'}_${mediaUrl}`;
  }

  /**
   * Get user context for Sentry
   */
  private getUserContext() {
    try {
      const currentUser = CurrentUserStore.user;
      return {
        id: currentUser?.id,
        email: currentUser?.email,
        role: currentUser?.role,
      };
    } catch {
      return {};
    }
  }

  /**
   * Get shop context for Sentry
   */
  private getShopContext() {
    try {
      const currentShop = ShopStore.currentShop;
      return {
        id: currentShop?.id,
        name: currentShop?.name,
      };
    } catch {
      return {};
    }
  }

  /**
   * Get connection type if available
   */
  private getConnectionType(): string {
    try {
      const connection =
        (navigator as any).connection ||
        (navigator as any).mozConnection ||
        (navigator as any).webkitConnection;
      return connection
        ? connection.effectiveType || connection.type || 'unknown'
        : 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Track performance metrics (could be used for analytics)
   */
  private trackMediaMetrics(metrics: MediaLoadMetrics): void {
    // Add breadcrumb for metrics
    Sentry.addBreadcrumb({
      message: `Media load metrics: ${metrics.success ? 'success' : 'failed'} in ${metrics.loadTime}ms`,
      category: 'performance',
      level: 'info',
      data: {
        ...metrics,
        timestamp: new Date().toISOString(),
      },
    });

    // For very slow loads (even successful ones), capture as warning
    if (metrics.success && metrics.loadTime > 5000) {
      Sentry.captureMessage('Slow media loading detected', {
        level: 'warning',
        tags: {
          component: 'MediaPreview',
          performance: 'slow-load',
        },
        extra: metrics as any,
      });
    }
  }

  /**
   * Clear all tracking data (useful for cleanup)
   */
  clearAllTracking(): void {
    // Clear all timeouts
    this.timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeoutIds.clear();
    this.loadStartTimes.clear();
  }
}

// Export singleton instance
export const mediaErrorTracker = new MediaErrorTrackingService();
export default mediaErrorTracker;
